import React from 'react';
import TabContext from '@mui/lab/TabContext';

import { useSelector } from 'react-redux';
import { TabList, TabPanel } from '@mui/lab';
import { Divider, Tab } from '@mui/material';

import { selectCommandByNodeId, State } from '@store/index';
import { NodeField, NodeFieldType } from '../../';
import { RequestParametersExpression } from '@common/Node/NodeSidebar/Custom/RequestParametersExpression';
import { RequestAuthentication } from '@common/Node/NodeSidebar/Custom';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeExecuteData } from '@common/Node/NodeSidebar/NodeData';
import { ArticleOutlined, SecurityOutlined, SettingsOutlined } from '@mui/icons-material';
import { Toggle } from '@common/Node/NodeSidebar/Custom/Toggle';
import { SchemaEditor } from '@common/Node/NodeSidebar/Custom/SchemaEditor';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
  dataLoading: boolean;
}

export const HTTPTabs: React.FC<Props> = ({ tab, setTab, dataLoading }) => {
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return null;

  const fields = command.fields;
  const state = command.state;

  const inputFields = fields.filter((field) => {
    return (field.type === NodeFieldType.InputExpression || field.type === NodeFieldType.Option) && field.tab === '1';
  });

  const settings = fields.filter((field) => {
    if (field.id === 'parse_response_as') return false;
    if (field.tab === '4') return true;
  });

  const parameters = fields.filter((field) => field.type === NodeFieldType.RequestParameterExpression);
  const authFields = fields.filter((field) => field.type === NodeFieldType.RequestAuth);
  const bodyFormat = fields.find((field) => field.id === 'body_format');
  const bodyJson = fields.find((field) => field.id === 'body_json');
  const bodyParameters = fields.find((field) => field.id === 'body_parameters');

  const method = state.method.toLowerCase();
  const bodyParameterMethods = ['post', 'put', 'patch'];
  const showBodyParams = bodyParameterMethods.includes(method);

  const renderBodyParameters = () => {
    if (!showBodyParams) return null;

    if (state?.body_format === 'form' || bodyFormat === undefined) {
      if (!bodyParameters) return null;
      return <RequestParametersExpression field={bodyParameters} command={command} />;
    }

    if (!bodyJson) return null;
    const enabled = state?.body_format === 'json';
    const nodeValue = state?.body_json?.value;
    return <SchemaEditor command={command} field={bodyJson} enabled={enabled} nodeValue={nodeValue} />;
  };

  return (
    <NodeSidebar>
      <TabContext value={tab}>
        <NodeSidebarTabsContainer color={command.config.background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disableRipple label='Node' value='1' />
            <Tab disableRipple title='Auth' iconPosition='start' icon={<SecurityOutlined />} value='2' />
            <Tab disableRipple title='Node Output' iconPosition='start' icon={<ArticleOutlined />} value='3' />
            <Tab disableRipple title='Settings' iconPosition='start' icon={<SettingsOutlined />} value='4' />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {inputFields.map((field: NodeField) => (
              <NodeInputExpression key={field.id} field={field} command={command} />
            ))}
            <Divider />
            {/* {workflowEndpoint && <WorkflowEndpoint command={command} field={workflowEndpoint} />} */}
            {parameters.map(
              (field: NodeField) =>
                field.id !== 'body_parameters' && (
                  <RequestParametersExpression key={field.id} field={field} command={command} />
                )
            )}
            {showBodyParams && bodyFormat && <Toggle command={command} field={bodyFormat} />}
            {renderBodyParameters()}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='2'>
          <NodeSidebarTab>
            {authFields.map((field: NodeField) => (
              <RequestAuthentication key={field.id} field={field} command={command} />
            ))}
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='3'>
          <NodeExecuteData
            command={command}
            loading={dataLoading}
            unknownData
            tooltip='This is the output of your last HTTP request from this node.'
          />
        </TabPanel>
        <TabPanel value='4'>
          <NodeSidebarTab>
            {settings.map((field: NodeField) => (
              <NodeInputExpression key={field.id} field={field} command={command} />
            ))}
          </NodeSidebarTab>
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
